/**
 * seenBadges.ts — a per-viewer, client-side record of which earned badges a person has already
 * LOOKED AT in their own Badge Panel. It exists to power one quiet affordance: a subtle "new" marker
 * on a freshly-earned badge that clears the moment the person opens the panel and sees it.
 *
 * WHY THIS IS NOT A NOTIFICATION, AND WHY IT LIVES HERE (PR-F46 / NT §8): badges are awarded silently
 * — "A person discovers a new badge when they next look at their own profile", and NT §8 makes the
 * silence deliberate ("Alerting on an achievement is the shortest route from recognition to
 * pressure"). So there is NO toast, NO pop-up, NO server push. The person still has to LOOK. This
 * module only helps them notice there is something to look at, on the profile page they chose to open
 * — a dot, then a word. Nothing is sent anywhere; nothing interrupts.
 *
 * WHY CLIENT-SIDE (§5.1 rule 1 — no new tracking): "seen" is a per-viewer, per-browser convenience,
 * not a fact about the person worth persisting on the server. Recording server-side WHICH badges
 * someone has looked at would be exactly the kind of new tracking §5.1 forbids. localStorage keeps it
 * where it belongs — on the one screen, for the one viewer — and it is fine for it to be lost (a new
 * browser simply re-surfaces the "new" marker once, harmless).
 *
 * ANTI-RANKING (PR-F58 / UR-F22 / PC-F31): this stores a SET of badge keys, never a count. Callers
 * derive only a boolean ("are there earned badges not in the seen set?") and a per-key flag — never a
 * number, total or score. `newBadgeKeys` returns the keys, not their tally, precisely so no caller is
 * tempted to render "N new".
 *
 * SCOPING — MUST NOT LEAK ACROSS PEOPLE: the key is namespaced by the signed-in user_id, mirroring
 * upload/draftStore.ts. Two people sharing a machine never see each other's "new" state.
 *
 * EVERY ACCESS IS DEFENSIVE, same as draftStore.ts: merely touching window.localStorage throws in
 * some privacy modes. On any failure we behave as "nothing seen yet is unknowable" → the marker
 * simply does not show. Losing the marker is invisible; crashing the profile page is not.
 */

/** Namespaced so it is obvious in devtools which app owns the key. v1 = the current shape (a key[]). */
const KEY_PREFIX = 'trailblazer:seen-badges:v1';

/** Per-user key: a shared browser must never surface one person's "new" badges to another. */
function seenKey(userId: string): string {
  return `${KEY_PREFIX}:${userId}`;
}

/**
 * The storage handle, or null when localStorage cannot be used at all (private mode, disabled
 * storage, quota). Wrapped because ACCESSING window.localStorage can itself throw — a truthiness
 * check is not enough. Null downstream means "no persistence" → the marker never shows, which is a
 * fine degradation (the person still discovers badges by opening the panel, exactly as PR-F46 says).
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const probe = `${KEY_PREFIX}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The set of badge keys this viewer has already seen in the panel, or an empty set when there is no
 * record / it cannot be read / it is malformed. Never throws.
 */
function readSeen(userId: string): Set<string> {
  const store = storage();
  if (!store || !userId) return new Set();
  let raw: string | null;
  try {
    raw = store.getItem(seenKey(userId));
  } catch {
    return new Set();
  }
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw) as unknown;
    // A tampered or old-shaped entry is treated as "nothing seen" rather than trusted. We only ever
    // keep string keys — anything else is dropped.
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((k): k is string => typeof k === 'string'));
  } catch {
    return new Set();
  }
}

/**
 * The earned badge keys this viewer has NOT yet seen in the panel — i.e. the "new" ones. Returns the
 * KEYS (not a count, by design — PR-F58) so a caller can flag those exact rows; an empty array means
 * nothing is new. `earnedKeys` is the current earned set from GET /api/me/badges (every row there is
 * earned). Order is not meaningful.
 */
export function newBadgeKeys(userId: string, earnedKeys: string[]): string[] {
  if (!userId || earnedKeys.length === 0) return [];
  const seen = readSeen(userId);
  return earnedKeys.filter((k) => !seen.has(k));
}

/**
 * Mark the given earned badge keys as seen — called when the person OPENS the Badge Panel (the act of
 * looking, which PR-F46 makes the moment of discovery). Merges into whatever was already recorded, so
 * a badge only ever moves from new → seen (opening the panel never "un-sees" another badge). Silently
 * no-ops when storage is unavailable; a marker that lingers is harmless, and autosave-style writes
 * must never surface an error. Pruned to the still-earned set so the record cannot grow without bound
 * (and a hypothetical future revocation would let a re-earned badge read as "new" again — correct).
 */
export function markBadgesSeen(userId: string, earnedKeys: string[]): void {
  const store = storage();
  if (!store || !userId) return;
  const merged = new Set(readSeen(userId));
  for (const k of earnedKeys) merged.add(k);
  // Keep only keys that are still earned, so the stored set tracks reality and stays bounded.
  const pruned = [...merged].filter((k) => earnedKeys.includes(k));
  try {
    store.setItem(seenKey(userId), JSON.stringify(pruned));
  } catch {
    // Quota or a mid-session permission change — nothing useful to do; the marker simply persists.
  }
}
